import { useCallback } from 'react';
import uniqid from 'uniqid';

import { getPromptMessageText } from 'containers/admin/clients/client/tabs-components/ai-assistant/helpers/message-helpers';

import {
  PART_TYPES,
  SPOOFED_CHART_DATASETS,
  SPOOFED_PROMPT_DATASETS,
} from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-assistant.constants';

import type { ChatMessage } from 'containers/admin/clients/client/tabs-components/ai-assistant/hooks/use-active-input-part';
import type { MarkPromptSource } from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-chat.types';

interface UseChatMessageInterceptorArgs {
  conversationId: string | null;
  setMessagesForConversation: (
    conversationId: string,
    updater: (messages: ChatMessage[]) => ChatMessage[]
  ) => void;
  originalSendMessage: (
    requestMessage: string | unknown,
    isRetrying?: boolean,
    source?: MarkPromptSource
  ) => void;
}

interface UseChatMessageInterceptorResult {
  interceptSendMessage: (
    requestMessage: string | unknown,
    isRetrying?: boolean,
    source?: MarkPromptSource
  ) => void;
}

// TODO: Remove once the backend streams parts-based guided prompts/charts.
const useChatMessageInterceptor = ({
  conversationId,
  setMessagesForConversation,
  originalSendMessage,
}: UseChatMessageInterceptorArgs): UseChatMessageInterceptorResult => {
  const appendSpoofedExchange = useCallback(
    (
      targetConversationId: string,
      requestText: string,
      aiText: string,
      parts: Array<{ type: string; data?: unknown }>
    ): void => {
      const userMessage: ChatMessage = {
        id: uniqid(),
        content: requestText,
        isUser: true,
      };

      setMessagesForConversation(targetConversationId, (previousMessages) => [
        ...previousMessages,
        userMessage,
      ]);

      setMessagesForConversation(targetConversationId, (previousMessages) => [
        ...previousMessages,
        {
          id: uniqid(),
          content: aiText,
          isUser: false,
          parts,
        },
      ]);
    },
    [setMessagesForConversation]
  );

  const interceptSendMessage = useCallback(
    (
      requestMessage: string | unknown,
      isRetrying = false,
      source?: MarkPromptSource
    ) => {
      const trimmedMessage =
        typeof requestMessage === 'string'
          ? requestMessage.trim()
          : requestMessage;

      const spoofedPrompts =
        typeof trimmedMessage === 'string'
          ? (
              SPOOFED_PROMPT_DATASETS as Record<
                string,
                Array<Record<string, unknown>>
              >
            )[trimmedMessage]
          : undefined;
      const spoofedChartDataset =
        typeof trimmedMessage === 'string'
          ? (
              SPOOFED_CHART_DATASETS as Record<
                string,
                { text: string; chart: Record<string, unknown> }
              >
            )[trimmedMessage]
          : undefined;

      if (conversationId && typeof trimmedMessage === 'string') {
        if (spoofedPrompts) {
          const generatedText = getPromptMessageText(spoofedPrompts.length);
          const messageParts = [
            { type: PART_TYPES.MESSAGE, data: { text: generatedText } },
            ...spoofedPrompts.map((prompt) => ({
              type: PART_TYPES.GUIDED_PROMPT,
              data: prompt,
            })),
          ];

          appendSpoofedExchange(
            conversationId,
            trimmedMessage,
            generatedText,
            messageParts
          );

          return;
        }

        if (spoofedChartDataset) {
          const messageParts = [
            {
              type: PART_TYPES.MESSAGE,
              data: { text: spoofedChartDataset.text },
            },
            { type: PART_TYPES.CHART, data: spoofedChartDataset.chart },
          ];

          appendSpoofedExchange(
            conversationId,
            trimmedMessage,
            spoofedChartDataset.text,
            messageParts
          );

          return;
        }
      }

      originalSendMessage(requestMessage, isRetrying, source);
    },
    [conversationId, appendSpoofedExchange, originalSendMessage]
  );

  return { interceptSendMessage };
};

export default useChatMessageInterceptor;
